import { useState, useEffect } from 'react';
import { Alert, Button, Group, Text } from '@mantine/core';
import { IconAlertCircle, IconX, IconSwitchHorizontal } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { impersonationUtils } from '@utils/impersonation';
import AsyncSelect from '@components/async-select/AsyncSelect';
import { useTheme } from '@contexts/ThemeContext';

export default function ImpersonationBanner() {
  const { colors: themeColors } = useTheme();
  const [loading, setLoading] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationInfo, setImpersonationInfo] = useState<any>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<any>(null);
  const [showSwitchUser, setShowSwitchUser] = useState(false);
  const [canImpersonate, setCanImpersonate] = useState(false);

  const updateImpersonationState = () => {
    const impersonating = impersonationUtils.isImpersonating();
    setIsImpersonating(impersonating);
    setImpersonationInfo(impersonationUtils.getImpersonationInfo());
    setImpersonatedUser(impersonationUtils.getImpersonatedUserInfo());

    try {
      const authDataRaw = localStorage.getItem(impersonating ? 'originalAuthData' : 'authData');
      if (authDataRaw) {
        const authData = JSON.parse(authDataRaw);
        const email = authData.email?.toLowerCase() || '';

        if (impersonating) {
          const originalEmail = authData.email?.toLowerCase() || '';
          setCanImpersonate(impersonationUtils.canImpersonate(originalEmail));
        } else {
          setCanImpersonate(impersonationUtils.canImpersonate(email));
        }
      } else {
        setCanImpersonate(false);
      }
    } catch {
      setCanImpersonate(false);
    }
  };

  useEffect(() => {
    updateImpersonationState();

    const handleImpersonationChange = () => {
      updateImpersonationState();
    };

    window.addEventListener('impersonationChanged', handleImpersonationChange);

    return () => {
      window.removeEventListener('impersonationChanged', handleImpersonationChange);
    };
  }, []);

  const handleSwitchUser = async (userId: string, userObject: any) => {
    if (!userId || !userObject?.origin?.id) return;

    setLoading(true);
    try {
      await impersonationUtils.stopImpersonation();
      await impersonationUtils.startImpersonation(userObject.origin.id, 'Admin impersonation switch');

      notifications.show({
        title: 'User Switch Successful',
        message: `Successfully switched to impersonating ${userObject.origin.email}`,
        color: 'green',
      });

      window.location.reload();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to switch user',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isImpersonating || !impersonationInfo) {
    return null;
  }

  const handleStopImpersonation = async () => {
    setLoading(true);
    try {
      await impersonationUtils.stopImpersonation();

      notifications.show({
        title: 'Impersonation Stopped',
        message: 'Successfully stopped impersonation',
        color: 'green',
      });

      window.location.reload();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to stop impersonation',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const bannerBg = themeColors.buttonBackground || themeColors.primary;
  const bannerFg = themeColors.buttonText;
  const actionButtonStyles = {
    root: {
      backgroundColor: themeColors.card,
      border: `1px solid ${themeColors.border}`,
      color: themeColors.text,
      '&:hover': {
        backgroundColor: themeColors.formFields || themeColors.dropdownHover || themeColors.card,
        borderColor: themeColors.accent || themeColors.primary,
        color: themeColors.primary,
      },
    },
  };

  return (
    <>
      <Alert
        icon={<IconAlertCircle size={16} />}
        variant="filled"
        className="mb-4 sticky top-0 z-50 shadow-md"
        styles={{
          root: {
            backgroundColor: bannerBg,
            border: `1px solid ${themeColors.buttonBorder || themeColors.accent || bannerBg}`,
          },
          icon: {
            color: bannerFg,
          },
          body: {
            color: bannerFg,
          },
          message: {
            color: bannerFg,
          },
        }}
      >
        <Group justify="space-between" align="center" w="100%">
          <div className="flex items-center gap-2">
            <Text size="sm" fw={600} style={{ color: bannerFg }}>
              You are impersonating: {impersonatedUser?.email || `User ID: ${impersonationInfo.targetUserId}`}
            </Text>
            {impersonatedUser?.name && (
              <Text size="xs" style={{ color: bannerFg, opacity: 0.85 }}>
                ({impersonatedUser.name})
              </Text>
            )}
            {impersonationInfo.reason && !impersonatedUser?.name && (
              <Text size="xs" style={{ color: bannerFg, opacity: 0.85 }}>
                ({impersonationInfo.reason})
              </Text>
            )}
          </div>

          <Group gap="xs">
            {canImpersonate && (
              <Button
                variant="outline"
                size="xs"
                leftSection={<IconSwitchHorizontal size={12} />}
                onClick={() => setShowSwitchUser(!showSwitchUser)}
                disabled={loading}
                styles={actionButtonStyles}
              >
                Switch User
              </Button>
            )}

            <Button
              variant="outline"
              size="xs"
              leftSection={<IconX size={12} />}
              onClick={handleStopImpersonation}
              loading={loading}
              styles={actionButtonStyles}
            >
              {loading ? 'Stopping...' : 'Stop Impersonation'}
            </Button>
          </Group>
        </Group>
      </Alert>

      {showSwitchUser && canImpersonate && (
        <div
          className="mb-4 mx-4 p-4 rounded-lg shadow-md"
          style={{
            backgroundColor: themeColors.card,
            border: `1px solid ${themeColors.border}`,
          }}
        >
          <Text size="sm" fw={600} mb="sm" style={{ color: themeColors.text }}>
            Switch to Another User
          </Text>
          <AsyncSelect
            label=""
            placeholder="Search users..."
            dynamicKeyParam="query"
            path="/api/v1/users"
            additionalParam="orderBy=email&ascending=true"
            onChange={handleSwitchUser}
            valueOnResponseKey="id"
            optionsLabelKeys={['email', 'name']}
            value=""
            disabled={loading}
          />
          <Text size="xs" mt="xs" style={{ color: themeColors.textSecondary }}>
            Select a user to switch impersonation to
          </Text>
        </div>
      )}
    </>
  );
}
